/**
 * Список покупок (дизайн — паттерн A2B: работает поверх ОБЗОРА, не трогая
 * машину состояний): пункты «товар@полка», обход одним туром с нумерованными
 * остановками, отметки «взял» с перестроением от текущей полки, обмен ссылкой
 * ?list= (+QR). Промо-блок «Сейчас в акции» — только фаза ОБЗОР (ADR 0019).
 */
import { formatDuration, getLang, tr } from '@app/i18n'
import { localized, type RouteStart, resolveStart, shelfCodeOf } from '@app/rvf'
import { useState } from 'preact/hooks'
import { importChunk } from '../lazy-retry'
import { promoPhase } from '../promo'
import { productPromoTiers } from '../search'
import { shareListUrl } from '../share-link'
import {
  addToList,
  buildShoppingTour,
  clearList,
  keyProductId,
  keyTargetId,
  removeFromList,
  type ShoppingTour,
  shoppingChecked,
  shoppingKeys,
  shoppingOpen,
  shoppingTour,
  toggleChecked,
} from '../shopping'
import { anchorId, geoStart, graph, langTick, venue, venueMap } from '../state'
import { featureName } from './labels'

/** Имя позиции: товар — имя товара, иначе имя цели. */
function keyLabel(v: NonNullable<typeof venue.value>, key: string): string {
  const productId = keyProductId(key)
  if (productId !== null) {
    const product = (v.products ?? []).find((p) => p.id === productId)
    if (product) return localized(product.name, getLang(), v.venue.default_language)
  }
  return featureName(v, keyTargetId(key))
}

/** Код полки позиции («R155») — печатается рядом с именем. */
function keyCode(v: NonNullable<typeof venue.value>, key: string): string | null {
  const poi = v.pois.find((p) => p.id === keyTargetId(key))
  return poi ? shelfCodeOf(poi) : null
}

function tourStart(v: NonNullable<typeof venue.value>): RouteStart | null {
  return resolveStart(v, {
    anchorId: anchorId.value ?? undefined,
    geo: geoStart.value ?? undefined,
  })
}

/** Касса-финиш: первая точка категории checkout (в демо — кассовая линия). */
function checkoutOf(v: NonNullable<typeof venue.value>): string | null {
  return v.pois.find((p) => p.category === 'checkout')?.id ?? null
}

function drawTour(tour: ShoppingTour | null): void {
  const map = venueMap.value
  if (!map) return
  if (!tour) {
    map.setRoute(null)
    map.setTourStops(null, null)
    return
  }
  const checked = shoppingChecked.value
  const firstOpen = tour.stops.find((s) => !s.keys.every((k) => checked.has(k)))
  map.setRoute(tour.composite)
  map.setTourStops(
    tour.stops.map((s) => ({
      n: s.n,
      point: s.point,
      level: s.level,
      done: s.keys.length > 0 && s.keys.every((k) => checked.has(k)),
      active: s === firstOpen,
    })),
    null,
  )
  map.fitRoute(tour.composite)
}

/** Построение обхода: от старта или от только что взятой полки. */
function rebuildTour(fromTargetId: string | null): void {
  const v = venue.value
  const g = graph.value
  if (!v || !g) return
  const remaining = shoppingKeys.value.filter((k) => !shoppingChecked.value.has(k))
  if (remaining.length === 0) {
    shoppingTour.value = null
    drawTour(null)
    return
  }
  let start = tourStart(v)
  if (fromTargetId !== null) {
    const at = v.pois.find((p) => p.id === fromTargetId)
    if (at) {
      const node = v.network.nodes.reduce<{ id: string; d: number } | null>((best, n) => {
        const d = (n.point[0] - at.point[0]) ** 2 + (n.point[1] - at.point[1]) ** 2
        return best === null || d < best.d ? { id: n.id, d } : best
      }, null)
      if (node) start = { point: at.point, nodeId: node.id, source: 'entrance' }
    }
  }
  if (!start) return
  const tour = buildShoppingTour(v, g, start, remaining, checkoutOf(v))
  shoppingTour.value = tour
  drawTour(tour)
}

function PromoSuggestions({ v }: { v: NonNullable<typeof venue.value> }) {
  // Реклама только в спокойном ОБЗОРЕ (ADR 0019) и только чего нет в списке.
  if (promoPhase() !== 'overview') return null
  const tiers = productPromoTiers(v)
  const inList = new Set(shoppingKeys.value)
  const offers = (v.products ?? [])
    .filter((p) => tiers.has(`${p.shelf}::${p.id}`) && !inList.has(`${p.shelf}::${p.id}`))
    .slice(0, 2)
  if (offers.length === 0) return null
  return (
    <div class="shop-promo">
      <p class="tech-label">{tr('shopping.promo_title')}</p>
      {offers.map((p) => (
        <div key={p.id} class="shop-promo-row">
          <span class="shop-promo-label">
            {localized(p.emphasis?.label, getLang(), v.venue.default_language)}
          </span>
          <span class="shop-row-name">
            {localized(p.name, getLang(), v.venue.default_language)}
          </span>
          <i class="promo-chip">{tr('promo.chip')}</i>
          <button
            type="button"
            class="icon-btn"
            aria-label={tr('shopping.add')}
            onClick={() => {
              addToList(v.venue.id, `${p.shelf}::${p.id}`)
            }}
          >
            +
          </button>
        </div>
      ))}
    </div>
  )
}

function ShareBlock({ v }: { v: NonNullable<typeof venue.value> }) {
  const [qr, setQr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const link = shareListUrl(v.venue.id, shoppingKeys.value)
  const share = (): void => {
    void navigator.clipboard?.writeText(link).then(() => setCopied(true))
    void importChunk(() => import('qrcode')).then(async (qrcode) => {
      setQr(await qrcode.toDataURL(link, { width: 360, margin: 1 }))
    })
  }
  return (
    <div class="shop-share">
      <button type="button" class="btn-secondary" onClick={share}>
        {copied ? tr('shopping.share_copied') : tr('shopping.share')}
      </button>
      {qr && <img class="shop-share-qr" src={qr} alt={tr('shopping.share')} />}
    </div>
  )
}

/** Плавающая кнопка списка с бейджем количества. */
export function ShoppingFab() {
  const v = venue.value
  if (!v || shoppingOpen.value) return null
  const count = shoppingKeys.value.length
  // Кнопка появляется там, где есть товарный слой или уже собранный список.
  if ((v.products ?? []).length === 0 && count === 0) return null
  return (
    <button
      type="button"
      class="shop-fab glass"
      aria-label={tr('shopping.title')}
      onClick={() => {
        shoppingOpen.value = true
      }}
    >
      🛒{count > 0 && <span class="shop-fab-badge">{count}</span>}
    </button>
  )
}

export function ShoppingPanel() {
  langTick.value
  const v = venue.value
  if (!v || !shoppingOpen.value) return null
  const keys = shoppingKeys.value
  const checked = shoppingChecked.value
  const tour = shoppingTour.value
  const openCount = keys.filter((k) => !checked.has(k)).length

  const close = (): void => {
    shoppingOpen.value = false
  }
  const hideTour = (): void => {
    shoppingTour.value = null
    drawTour(null)
  }

  return (
    <aside class="shop-panel glass anim-drawer">
      <div class="shop-head">
        <div>
          <p class="tech-label">{tr('shopping.kicker')}</p>
          <h2 class="shop-title">{tr('shopping.title')}</h2>
        </div>
        <button type="button" class="icon-btn" aria-label={tr('common.close')} onClick={close}>
          ✕
        </button>
      </div>

      {keys.length === 0 && <p class="shop-empty">{tr('shopping.empty')}</p>}

      <div class="shop-list">
        {keys.map((key) => (
          <div key={key} class={`shop-row${checked.has(key) ? ' shop-row-done' : ''}`}>
            <label class="shop-row-check">
              <input
                type="checkbox"
                checked={checked.has(key)}
                onChange={() => {
                  toggleChecked(v.venue.id, key)
                  if (shoppingTour.value) rebuildTour(keyTargetId(key))
                }}
              />
              <span class="shop-row-name">{keyLabel(v, key)}</span>
            </label>
            {keyCode(v, key) && <code class="shop-row-code">{keyCode(v, key)}</code>}
            <button
              type="button"
              class="icon-btn"
              aria-label={tr('shopping.remove')}
              onClick={() => {
                removeFromList(v.venue.id, key)
                if (shoppingTour.value) rebuildTour(null)
              }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      <PromoSuggestions v={v} />

      {keys.length > 0 && (
        <div class="shop-actions">
          {tour ? (
            <button type="button" class="btn-secondary" onClick={hideTour}>
              {tr('shopping.tour_hide')}
            </button>
          ) : (
            <button
              type="button"
              class="btn-primary"
              disabled={openCount === 0}
              onClick={() => rebuildTour(null)}
            >
              {tr('shopping.tour_btn', { n: String(openCount) })}
            </button>
          )}
          {tour && (
            <p class="shop-tour-sum">
              {tr('shopping.stops_n', { n: String(tour.stops.length) })} · ~
              {formatDuration(tour.plan.durationS)}
            </p>
          )}
          <ShareBlock v={v} />
          <button
            type="button"
            class="shop-clear"
            onClick={() => {
              clearList(v.venue.id)
              drawTour(null)
            }}
          >
            {tr('shopping.clear')}
          </button>
        </div>
      )}
    </aside>
  )
}
